import { useState } from "react";
import { useErrorDisplay } from "../components/ErrorDisplay"
import ProficiencyGroupClient, { ProficiencyGroup, ProficiencyGroupErrors } from "../apiClients/ProficiencyGroupClient"
import { useEffect } from "react";
import { useRef } from "react";
import BusySpinner from "../components/BusySpinner";
import { ButtonGroup, Typography } from "@mui/material";
import EditProficiencyGroupPopup from "../popups/EditProficiencyGroupPopup";
import { DataGridPro } from "@mui/x-data-grid-pro";
import PanelSegment from "../components/PanelSegment";
import { useMainDisplayHeight } from "../components/WindowDimensions";
import OnlyForRole from "../components/OnlyForRole";
import { Securables } from "../Enums";
import PageTitle from "../components/PageTitle";
import { I18N } from "../I18N";
import MobileEntityCard from "../components/responsive/MobileEntityCard";
import ResponsiveCollection from "../components/responsive/ResponsiveCollection";
import useViewportMode from "../hooks/useViewportMode";


export interface ProficiencyGroupManagementPageProps {
    embedded?: boolean;
}

const ProficiencyGroupManagementPage = ({ embedded = false }: ProficiencyGroupManagementPageProps) => {
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [proficiencyGroups, setProficiencyGroups] = useState<ProficiencyGroup[]>([]);
    const [busy, setBusy] = useState<boolean>(false);
    const initialized = useRef<boolean>();
    const { mode: viewportMode } = useViewportMode();
    const mainDisplayHeight = useMainDisplayHeight();

    const loadData = async () => {
        try {
            setBusy(true);
            
            let results = await new ProficiencyGroupClient().GetAll();
            setProficiencyGroups(results);

        } catch (error) {
            setError(error, ProficiencyGroupErrors.Get);
        }
        finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (initialized.current === true)
            return;

        initialized.current = true;
        loadData();

    });

    const renderGroupCard = (group: ProficiencyGroup) => (
        <MobileEntityCard
            ariaLabel={`Proficiency group: ${group.Name}`}
            title={group.Name}
            fields={[
                { label: I18N.Translate('Weight'), value: group.Weight },
                { label: I18N.Translate('Type'), value: I18N.Translate(String(group.Type)) },
            ]}
            actions={
                <OnlyForRole role={Securables.profgroup_modify}>
                    <EditProficiencyGroupPopup
                        proficiencyId={group.Id}
                        proficiencyName={group.Name}
                        proficiencyWeight={group.Weight}
                        proficiencyType={group.Type}
                        onChange={loadData}
                        aria-label={`${I18N.Translate('Edit Proficiency Group')}: ${group.Name}`}
                    />
                </OnlyForRole>
            }
        />
    );

    const desktopGrid = (
        <div style={{ height: mainDisplayHeight, width: '100%' }}>
            <DataGridPro
                density="compact"
                rows={proficiencyGroups.map(x => { return { ...x, id: x.Id } })}
                columns={[
                    {
                        field: 'Action',
                        sortable: false,
                        filterable: false,
                        resizable: false,
                        width: 70,
                        align: 'center',
                        disableColumnMenu: true,
                        renderCell: (params) =>
                            <ButtonGroup>
                                <OnlyForRole role={Securables.profgroup_modify}>
                                    <EditProficiencyGroupPopup
                                    proficiencyId={params.row.Id}
                                    proficiencyName={params.row.Name}
                                    proficiencyWeight={params.row.Weight}
                                    proficiencyType={params.row.Type}
                                    onChange={loadData} />
                                    </OnlyForRole>
                            </ButtonGroup>
                    },
                    {
                        field: 'Name',
                        headerName: 'Name',
                        flex: 1,
                        sortable: false
                    },
                    {
                        field: 'Weight',
                        headerName: 'Weight',
                        flex: 1,
                        sortable: false
                    },
                ]}
            />
        </div>
    );


    const collection = <ResponsiveCollection
        viewMode={viewportMode}
        items={proficiencyGroups}
        getKey={group => group.Id}
        renderCard={renderGroupCard}
        desktop={desktopGrid}
        ariaLabel={I18N.Translate('Proficiency Groups')}
        emptyState={<Typography sx={{ p: 2 }} color="text.secondary">{I18N.Translate('No proficiency groups found')}</Typography>}
    />;

    return <>
            <ErrorDisplay />
            <BusySpinner busy={busy}>
                {embedded
                    ? collection
                    : <PanelSegment titleContent={<PageTitle title={I18N.Translate('Proficiency Group Management')} />} fillHeight>
                        {collection}
                    </PanelSegment>}
            </BusySpinner>
        </>;
}

export default ProficiencyGroupManagementPage;
